import { useId } from 'react';
import { Flame, Hammer, Sprout, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { IDifficulty } from '@/redux/post/post.d';
import { redlineGroup } from './FieldError';

const DIFFICULTIES: Array<{
  level: IDifficulty;
  Icon: LucideIcon;
  tone: string;
}> = [
  { level: IDifficulty.BEGINNER, Icon: Sprout, tone: 'text-success' },
  { level: IDifficulty.ADVANCED, Icon: Hammer, tone: 'text-warning' },
  { level: IDifficulty.EXPERT, Icon: Flame, tone: 'text-destructive' },
];

/** Three tiles rather than a dropdown: the scale is fixed at three, and behind a
 * dropdown its colour only exists while the menu is open. */
export default function DifficultyPicker({
  value,
  onChange,
  invalid,
  label = 'Project Difficulty',
}: {
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
  label?: string;
}) {
  const uid = useId();
  return (
    <RadioGroup
      aria-label={label}
      value={value}
      onValueChange={onChange}
      className={cn(
        'grid grid-cols-1 gap-2 sm:grid-cols-3',
        redlineGroup(invalid)
      )}
    >
      {DIFFICULTIES.map(({ level, Icon, tone }) => (
        <label
          key={level}
          htmlFor={`${uid}-${level}`}
          className={cn(
            'flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-2 transition-[background-color,border-color,box-shadow] duration-fast',
            value === level
              ? 'border-primary bg-accent shadow-[0_0_0_1px] shadow-primary'
              : 'border-border bg-card hover:bg-muted/40'
          )}
        >
          {/* Its only content is the dot, so without this the radio has no
              accessible name at all. */}
          <RadioGroupItem
            id={`${uid}-${level}`}
            value={level}
            aria-label={level}
            className="border-muted-foreground/40 data-[state=checked]:border-primary"
          />
          <Icon className={cn('h-4 w-4 shrink-0', tone)} />
          <span className="truncate text-[13px] font-medium">{level}</span>
        </label>
      ))}
    </RadioGroup>
  );
}
